import React, { useState } from 'react'
import { differenceBy } from 'lodash/fp'
import { compose, defaultProps, mapProps, withProps } from 'recompose'
import { MultiSelect as CoreMultiSelect, Select as CoreSelect, Suggest } from '@blueprintjs/select'
import { DateInput as CoreDateInput, DatePicker as CoreDatePicker } from '@blueprintjs/datetime'
import { default as CoredMaskedInput } from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import "@blueprintjs/datetime/lib/css/blueprint-datetime.css"
import { Popover, Position } from '../Popover'
import moment from 'moment'
import _ from 'lodash'
import "./index.css"

import {
  Button,
  Checkbox as CoreCheckbox,
  EditableText as CoreEditableText,
  FormGroup,
  HTMLSelect,
  Icon,
  InputGroup,
  Intent,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  TextArea
} from '@blueprintjs/core'

const editableStyle = {
  borderBottom: '2px dashed grey',
  marginLeft: 5,
  paddingLeft: 5,
  paddingBottom: 5
}

const editableIconStyle = {
  marginLeft: 5
}

const END_OF_YEAR_AFTER_NEXT = new Date(moment().year() + 2, 11, 31)

export const FormDate = ({
  groupStyles = {},
  disabled,
  errorText,
  helpText,
  label,
  value,
  className,
  ...props
}) => {
  const val = typeof value === 'string' ? value.replace(/-/g, '/') : value
  const cleanedValue = value instanceof Date || value === null ? value
    : value === '' || value === undefined ? null
      : new Date(val)

  const preventBadInput = (e) => {
    if (!/^[0-9/]+$/gm.test(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
      className={className}
    >
      <CoreDateInput
        formatDate={date => {
          return date.toLocaleDateString()
        }}
        parseDate={(str, locale) => {
          return moment(str, ['M/D/YYYY', 'M/D/YY'], true).toDate()
        }}
        noValidate
        disabled={disabled}
        placeholder={"M/D/YYYY"}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        popoverProps={{ position: props?.popoverPosition || 'auto', disabled: props.disablePopover }}
        defaultValue={cleanedValue}
        className={className}
        maxDate={END_OF_YEAR_AFTER_NEXT}
        inputProps={{
          onKeyPress: preventBadInput,
          ...props
        }}
        dayPickerProps={{
          onDayClick: (value) => {
            props.onBlur(moment(value).format('YYYY-MM-DD'))
          }
        }}
        onError={(errorDate) => {
          props.onBlur(errorDate)
        }}
        value={cleanedValue}
        {...props}
      />
    </FormGroup>
  )
}


export const FormEditableText = ({ errorText, ...props }) => {
  return (
    <span style={editableStyle}>
      <CoreEditableText
        multiline={false}
        minWidth={200}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        {...props}
      />

      <Icon
        icon="edit"
        style={editableIconStyle}
        intent={errorText ? Intent.DANGER : Intent.NONE}
      />
    </span>
  )
}

export { InputGroup, FormGroup }

export const FormRadioGroup = ({
  groupStyles = {},
  items,
  disabled,
  errorText,
  helpText,
  label,
  placeholder,
  value = '',
  ...props
}) => {
  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
    >
      <RadioGroup
        noValidate
        disabled={disabled}
        placeholder={placeholder}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        selectedValue={((value && value.id) || '')}
        {...props}
      >
        {items.map((item, idx) => <Radio key={`${props.key}_${idx}`} label={item.name} value={item.id} />)}
      </RadioGroup>

    </FormGroup>
  )
}

export const FormYesNoRadioGroup = ({
  groupStyles = {},
  items,
  disabled,
  errorText,
  helpText,
  label,
  placeholder,
  value = '',
  ...props
}) => {

  const getSelectionToRender = (value) => {
    if (value === true) {
      return 1
    } else if (value === false) {
      return 0
    } else {
      return ''
    }
  }

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
    >
      <RadioGroup
        noValidate
        disabled={disabled}
        placeholder={placeholder}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        selectedValue={getSelectionToRender(value)}
        {...props}
      >
        {items.map((item, idx) => <Radio key={`${props.key}_${idx}`} label={item.name} value={item.id} />)}
      </RadioGroup>

    </FormGroup>
  )
}

export const FormInput = ({
  groupStyles = {},
  disabled,
  errorText,
  helpText,
  label,
  type = '',
  placeholder,
  value = '',
  className,
  ...props
}) => {
  const isNumeric = type === "number"

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
      className={className}
    >
      <InputGroup
        type={type}
        noValidate
        disabled={disabled}
        placeholder={placeholder}
        autoComplete='off'
        intent={errorText ? Intent.DANGER : Intent.NONE}
        value={(value || value === 0 ? value : '')}
        className={className}
        pattern={isNumeric ? "[0-9]*" : undefined}
        {...props}
      />
    </FormGroup>
  )
}

export const MaskedInput = ({
  disabled,
  errorText,
  helpText,
  label,
  placeholder,
  groupStyles,
  mask,
  value = '',
  ...props
}) => {
  return (
    <FormGroup
      disabled={disabled}
      label={label}
      style={groupStyles}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
    >
      <CoredMaskedInput
        guide={false}
        mask={mask}
        value={(value || value === 0 ? value.toLocaleString() : '')}
        {...props}
        render={(ref, props) =>
          <InputGroup
            noValidate
            disabled={disabled}
            placeholder={placeholder}
            intent={errorText ? Intent.DANGER : Intent.NONE}
            inputRef={ref}
            {...props}
          />
        }
      />
    </FormGroup>
  )
}

export const PhoneMaskedInput = withProps({
  mask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
})(MaskedInput)

const thousandsMask = createNumberMask({
  prefix: '',
  allowNegative: true
})

const currencyMask = createNumberMask({
  prefix: '$',
  allowDecimal: true,
  includeThousandsSeparator: true
})

export const NumberMaskedInput = withProps({
  mask: thousandsMask
})(MaskedInput)

export const CurrencyMaskedInput = withProps({
  mask: currencyMask
})(MaskedInput)

export const FormTextArea = ({ disabled, errorText, helpText, label, placeholder, value = '', ...props }) => (
  <FormGroup
    disabled={disabled}
    label={label}
    helperText={errorText || helpText}
    intent={errorText ? Intent.DANGER : Intent.NONE}
  >
    <TextArea
      fill
      growVertically={true}
      disabled={disabled}
      value={value || ''}
      style={{ minHeight: '3em' }}
      {...props}
    />
  </FormGroup>
)


const itemValueRenderer = (item) => item.name

const tagRenderer = (item) => item.name

const itemRenderer = (item, { handleClick, modifiers }) => (
  <MenuItem
    active={modifiers.active}
    key={item.id}
    text={item.name}
    onClick={(e) => handleClick(item, e)}
    style={{ minHeight: 30 }}
  />
)

const itemsEqual = (a, b) => a.name === b.name

const ClearButton = ({ disabled, ...props }) => (
  <Button
    minimal
    icon="small-cross"
    disabled={disabled}
    {...props}
  />
)

const filterItemsStartsWithThenContains = (query, items, maxListSize, disableMaxListSize = false) => {
  let maximumListSize = disableMaxListSize
    ? items?.length || 0
    : maxListSize || DEFAULT_MAX_LIST_SIZE

  if (!query) {
    return items.slice(0, maximumListSize)
  } else {
    const startsWith = _.orderBy(items.filter((i) => i?.name?.toLowerCase().indexOf(query?.toLowerCase()) === 0), [(o) => o.name], ['asc'])
    const contains = _.orderBy(items.filter((i) => i?.name?.toLowerCase().indexOf(query?.toLowerCase()) > 0), [(o) => o.name], ['asc'])

    return startsWith.concat(contains).slice(0, maximumListSize)
  }
}

export const FormSuggestWithCreate = ({
  disabled,
  style = {},
  errorText,
  maxListSize = DEFAULT_MAX_LIST_SIZE,
  helpText,
  label,
  items = [],
  onBlur,
  onChange,
  value,
  disableMaxListSize,
  ...props
}) => {
  const onClear = () => onChange(null)

  const selectedItem = (items, value) => {
    let match = items.find(i => i.name === value)
    return match ? match : null
  }

  const createItem = (query) => {
    return {
      id: 0,
      name: query
    }

  }
  const renderCreateItem = (query, active, { handleClick }) => (
    <MenuItem
      icon="add"
      text={`Create "${query}"`}
      active={active}
      onClick={(e) => addNewItems(query, e)}
      shouldDismissPopover={true}
    />
  )

  const addNewItems = (query, e) => {
    let newItem = createItem(query)

    if (!items.find(i => i.name === query)) {
      items.push(newItem)
    }
    return onChange(newItem, e)
  }

  return (
    <div style={style}>
      <FormGroup
        disabled={disabled}
        label={label}
        helperText={errorText || helpText}
        intent={errorText ? Intent.DANGER : Intent.NONE}
      >
        <Suggest
          fill
          items={items}
          itemsEqual={itemsEqual}
          itemRenderer={itemRenderer}
          inputValueRenderer={itemValueRenderer}
          selectedItem={selectedItem(items, value)}
          onItemSelect={onChange}
          inputProps={{
            ...props,
            onBlur,
            autoComplete: 'off',
            disabled,
            intent: errorText ? Intent.DANGER : Intent.NONE,
            rightElement: <ClearButton disabled={disabled} onClick={onClear} />
          }}
          query={value || ''}
          itemListPredicate={(query, items) => filterItemsStartsWithThenContains(query, items, disableMaxListSize ? Number.MAX_VALUE : maxListSize)}
          createNewItemFromQuery={createItem}
          createNewItemRenderer={renderCreateItem}
          noResults={<MenuItem disabled={true} text="No results" />}
          resetOnQuery={true}
          popoverProps={{
            popoverClassName: disableMaxListSize ? "scrollable-popover" : ""
          }}
        />
      </FormGroup>
    </div>
  )
}

export const FormSuggest = ({
  disabled,
  style = {},
  errorText,
  helpText,
  label,
  items = [],
  onBlur,
  maxListSize = DEFAULT_MAX_LIST_SIZE,
  onChange,
  value,
  disableMaxListSize,
  ...props
}) => {
  const onClear = () => onChange(null)
  return (
    <div style={style}>
      <FormGroup
        disabled={disabled}
        label={label}
        helperText={errorText || helpText}
        intent={errorText ? Intent.DANGER : Intent.NONE}
      >
        <Suggest
          fill
          items={items}
          itemRenderer={itemRenderer}
          inputValueRenderer={itemValueRenderer}
          selectedItem={value}
          inputProps={{
            ...props,
            onBlur,
            autoComplete: 'off',
            disabled,
            intent: errorText ? Intent.DANGER : Intent.NONE,
            rightElement: <ClearButton disabled={disabled} onClick={onClear} />
          }}
          query={ value?.name || ''}
          itemListPredicate={(query, items) => filterItemsStartsWithThenContains(query, items, disableMaxListSize ? Number.MAX_VALUE : maxListSize)}
          onItemSelect={onChange}
          popoverProps={{
            popoverClassName: disableMaxListSize ? "scrollable-popover" : ""
          }}
        />
      </FormGroup>
    </div>
  )
}

export const FormMultiSuggest = ({
  disabled,
  errorText,
  helpText,
  label,
  name,
  items = [],
  onBlur,
  onChange,
  value = [],
  ...props
}) => {
  const onItemSelect = (item) => onChange(value.concat(item))

  const onRemove = (_, index) => onChange(value.filter((_, i) => i !== index))

  const onClear = () => onChange([])

  const filteredItems = differenceBy('id', items, value)

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
    >
      <CoreMultiSelect
        fill
        items={filteredItems}
        itemRenderer={itemRenderer}
        inputValueRenderer={itemValueRenderer}
        onItemSelect={onItemSelect}
        selectedItems={value}
        tagRenderer={tagRenderer}
        tagInputProps={{
          ...props,
          disabled,
          onRemove,
          rightElement: <ClearButton disabled={disabled} onClick={onClear} />,
          tagProps: {
            intent: Intent.PRIMARY
          },
          inputProps: {
            autoComplete: 'none',
            name,
            onBlur,
          }
        }}
      />
    </FormGroup>
  )
}

const toOptions = (items = [], label_property) => {
  const mappedItems = items.map((props) => {
    return {
      label: label_property ? props[label_property] : props['name'],
      value: props.id
    }
  })

  return mappedItems
}


const renderItemList = ({ filteredItems, itemsParentRef, query, renderItem }) => {
  const itemsToRender = filteredItems.map(renderItem).filter(item => item != null);

  return (
    <Menu style={{ overflow: 'auto', maxHeight: '15em' }} ulRef={itemsParentRef}>
      {query &&
        <MenuItem
          disabled={true}
          text={`Found ${itemsToRender.length} items matching "${query}"`}
        />}
      {itemsToRender}
    </Menu>
  );
}

const filterItemList = (listMaxSize, disableMaxListSize, label_property = null, query, items) => {
  const normalizedQuery = query.toLowerCase()
  const startsWithQuery = []
  const containsButNotStartsWithQuery = []

  items.forEach(item => {
    const normalizedName = label_property ? item[label_property].toLowerCase() : item.name.toLowerCase()
    if (normalizedName.indexOf(normalizedQuery) === 0) {
      startsWithQuery.push(item)
    } else if (normalizedName.indexOf(normalizedQuery) > 0) {
      containsButNotStartsWithQuery.push(item)
    } else return
  })

  const filtered = _.orderBy(startsWithQuery, [(o) => o.name], ['asc']).concat(_.orderBy(containsButNotStartsWithQuery, [(o) => o.name], ['asc']))

  const mappedAndFiltered = !label_property
    ? filtered
    : filtered.map(i => ({ ...i, name: i[label_property] }))
  return disableMaxListSize
    ? mappedAndFiltered
    : mappedAndFiltered.slice(0, listMaxSize)
}

const DEFAULT_MAX_LIST_SIZE = 10

export const FormMultiSelect = ({ value, ...props }) => (
  <FormGroup
    disabled={props.disabled}
    label={props.label}
    helperText={props.errorText || props.helpText}
    intent={props.errorText ? Intent.DANGER : Intent.NONE}
  >
    <BaseMultiSelect value={value ? value : []} {...props} />
  </FormGroup>
)

export const BaseMultiSelect = compose(defaultProps({
  fill: true,
  large: true,
  loading: false,
  resetOnSelect: true,
  openOnKeyDown: false,
  noResults: 'no results',
  itemRenderer: itemRenderer,
  itemListRenderer: renderItemList
}),
  mapProps(({
    id,
    loading,
    items = [],
    placeholder,
    style,
    label_property,
    maxListSize = DEFAULT_MAX_LIST_SIZE,
    disableMaxListSize,
    tabIndex,
    value = [],
    openOnKeyDown,
    onChange,
    activeOnly,
    ...props
  }) => ({
    ...props,
    id,
    itemListPredicate: filterItemList.bind(null, maxListSize, disableMaxListSize, label_property),
    items: activeOnly ? items.filter(i => i.is_active && !value.some(v => v.id === i.id)) : items.filter(i => !value.some(v => v.id === i.id)),
    selectedItems: value,
    className: loading ? 'bp3-skeleton' : null,
    disabled: loading || props.disabled,
    tabIndex: loading ? -1 : tabIndex,
    tagRenderer: (item) => props.label_property ? item[label_property] : item.name,
    tagInputProps: {
      tagProps: (value) => ({
        intent: !activeOnly || items.find(i => props.label_property ? i[label_property] === value : i.name === value).is_active
          ? 'primary'
          : 'warning'
      }),
      onRemove: (_, index) => {
        const newValue = value.filter((v, i) => i !== index)
        onChange(newValue)
      },
      disabled: loading || props.disabled
    },
    onItemSelect: (item) => onChange(value.concat(item))
  })))(CoreMultiSelect)


export const FormSimpleSelect = ({
  groupStyles = {},
  withOther = false,
  disabled,
  errorText,
  helpText,
  items,
  label,
  label_property,
  nullable,
  onChange,
  value,
  ...props
}) => {
  const mapped = toOptions(items, label_property)
  let options = nullable ? [{ label: '', value: '' }].concat(mapped) : mapped
  options = withOther ? options.concat([{ label: 'Other', value: 99999, id: 99999 }]) : options

  const handleChange = (evt) => {
    const item = evt.target.value === '99999'
      ? { id: 99999, name: 'Other' }
      // eslint-disable-next-line
      : items.find(o => o.id == evt.target.value)

    onChange(item)
  }

  const id = value?.id || ''

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
    >
      <HTMLSelect
        fill
        autoComplete="off"
        disabled={disabled}
        options={options}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        onChange={handleChange}
        value={id}
        {...props}
      />
    </FormGroup>
  )
}

export const FormSelect = ({
  disabled,
  errorText,
  helpText,
  items = [],
  label,
  nullable,
  onChange,
  value,
  customItemRenderer,
  maxListSize,
  disableMaxListSize,
  preFilterPredicateKey,
  ...props
}) => {
  const handleChange = (e) => {
    // eslint-disable-next-line
    const item = items.find(o => o.id == e.id)
    onChange(item)
  }
  const id = value && value.id
  // eslint-disable-next-line
  const selectedValueText = items.find(i => i.id == id) ? items.find(i => i.id == id).name : 'Select an item...   '

  items = nullable ? [{ id: null, name: '' }].concat(items) : items

  const preFilteredItems = preFilterPredicateKey
    ? items.filter(i => i[preFilterPredicateKey])
    : items

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
    >
      <CoreSelect
        fill
        filterable
        value={id}
        items={items}
        disabled={disabled}
        onChange={handleChange}
        itemRenderer={customItemRenderer ? customItemRenderer : itemRenderer}
        itemListPredicate={(query, items) => filterItemsStartsWithThenContains(query, preFilteredItems, DEFAULT_MAX_LIST_SIZE, disableMaxListSize)}
        itemListRenderer={renderItemList}
        onItemSelect={handleChange}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        {...props}
      >
        <Button text={selectedValueText} rightIcon='double-caret-vertical' disabled={disabled} style={{ minWidth: 100 }} />
      </CoreSelect>
    </FormGroup>
  )
}

export const FormDateTimeInput = ({
  groupStyles = {},
  disabled,
  errorText,
  helpText,
  label,
  type = '',
  placeholder,
  value = '',
  ...props
}) => {
  const safeValue = value
    ? typeof value.getMonth === 'function'
      ? value
      : new Date(value)
    : null

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
    >
      <CoreDatePicker
        {...props}
        timePickerProps={{
          timePrecision: props.timePrecision || 'minute',
          useAmPm: true
        }}
        type={type}
        noValidate
        disabled={disabled}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        value={safeValue || null}
      />
    </FormGroup>
  )
}

export const CheckboxInput = ({ groupStyles = {}, disabled, errorText, helpText, label, checked, ...props }) => {

  return (
    <FormGroup
      disabled={disabled}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
    >
      <CoreCheckbox
        {...props}
        disabled={disabled}
        checked={checked}
        intent={errorText ? Intent.DANGER : Intent.NONE}
        large
        label={label}
      />
    </FormGroup>
  )
}

export const CalendarDatePicker = ({ ...props }) => {
  return (
    <CoreDatePicker
      {...props}
    />
  )
}

export const FormCustomDateTimePicker = ({
  disabled,
  groupStyles = {},
  errorText,
  helpText,
  label,
  value,
  placeholder,
  step,
  onDateTimeChange,
  onTextChange,
  onBlur,
  ...props
}) => {

  if (value && (moment(value, 'YYYY-MM-DDTHH:mm:ss', true).isValid() || moment(value, 'YYYY-MM-DD HH:mm:ss', true).isValid())) {
    value = moment(value).format('MM/DD/YYYY hh:mm A')
  }
  const [timeNow, setTimeNow] = useState(moment())
  const amPmList = [{ id: 1, name: 'AM' }, { id: 2, name: 'PM' }]
  const hourList = []
  const minuteList = []

  const buildTimeList = (list, startVal, endVal, incrementVal) => {
    for (var i = startVal; i <= endVal; i += incrementVal) {
      list.push({
        id: i,
        name: ('0' + i.toString()).slice(i.toString().length - 1)
      })
    }
  }

  buildTimeList(hourList, 1, 12, 1)
  buildTimeList(minuteList, 0, 59,
    (step && parseInt(step) && 60 % (parseInt(step) / 60) === 0) //Check if the step provided is a factor of 60
      ? (parseInt(step) / 60)
      : 1
  )

  const roundTime = (step, date) => {
    if (!step) return date

    //Round to the next stepped minunte
    const stepInMinutes = parseInt(step) / 60
    const currentMinutes = parseInt(moment(date).format('mm'))
    const newMinutes = Math.round(Math.ceil(currentMinutes / stepInMinutes)) * stepInMinutes
    const newDateTime = newMinutes === 60 ? moment(date).add(1, 'hour').minutes(0) : moment(date).minutes(newMinutes)

    return newDateTime

  }

  const setTime = (selectedItem, source, value, step) => {
    const currentDate = !value ? roundTime(step, timeNow) : moment(value)
    const parsedValue = source !== 'ampm' ? parseInt(selectedItem.name) : null

    switch (source) {
      case 'hour':
        currentDate.hours() >= 12
          ? currentDate.hours(parsedValue === 12 ? parsedValue : parsedValue + 12)
          : currentDate.hours(parsedValue === 12 ? 0 : parsedValue)
        break
      case 'minute':
        currentDate.minutes(parseInt(parseInt(parsedValue)))
        break
      case 'ampm':
        selectedItem.id === 1 ? currentDate.subtract(12, 'hours') : currentDate.add(12, 'hours')
        break
      default:
        break
    }
    onDateTimeChange(currentDate.format('YYYY-MM-DDTHH:mm:ss'))
  }

  const validateInput = (e, step) => {
    const input = e.currentTarget.value

    if (input && ((moment(input, 'MM/DD/YYYY hh:mm A', true).isValid()) || moment(input, 'YYYY-MM-DD HH:mm:ss', true).isValid())) {
      onDateTimeChange(
        step && minuteList.findIndex(m => parseInt(m.name) === parseInt(moment(input).format('mm')) === -1)
          ? roundTime(step, input).format('YYYY-MM-DDTHH:mm:ss')
          : moment(input).format('YYYY-MM-DDTHH:mm:ss'))
    }
    else {
      onDateTimeChange('')
    }
  }

  const CalendarPopover = (value, step) => (
    <Popover
      onOpened={() => setTimeNow(moment())}
      content={
        <>
          <CalendarDatePicker
            maxDate={END_OF_YEAR_AFTER_NEXT}
            highlightCurrentDay={true}
            onChange={(chosenDate, isUserSelected) => {
              if (chosenDate && isUserSelected) {

                const currentTime = !value ? roundTime(step, timeNow) : moment(value)
                const newDateTime = moment(chosenDate).hours(moment(currentTime).hours()).minutes(moment(currentTime).minutes()).format('YYYY-MM-DDTHH:mm:ss')

                onDateTimeChange(newDateTime)
              }
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: 3 }}>
            <FormSimpleSelect
              items={hourList}
              onChange={(selectedValue) => {
                setTime(selectedValue, 'hour', value, step)
              }}
              value={hourList.find(h => h.name === (!value ? roundTime(step, timeNow) : moment(value)).format('hh'))}
              groupStyles={{ paddingRight: 10 }}
            />
            <FormSimpleSelect
              items={minuteList}
              onChange={(selectedValue) => {
                setTime(selectedValue, 'minute', value, step);
              }}
              value={minuteList.find(m => m.name === (!value ? roundTime(step, timeNow) : moment(value)).format('mm'))}
              groupStyles={{ paddingRight: 10 }}
            />
            <FormSimpleSelect
              items={amPmList}
              onChange={(selectedValue) => {
                setTime(selectedValue, 'ampm', value, step);
              }}
              value={amPmList.find(h => h.name === moment(!value ? timeNow : value).format('A'))}
            />
          </div>
        </>
      }
      disabled={disabled}
      position={Position.BOTTOM_RIGHT}
    >
      <Button minimal icon='calendar' />
    </Popover >
  );

  return (
    <FormGroup
      disabled={disabled}
      label={label}
      helperText={errorText || helpText}
      intent={errorText ? Intent.DANGER : Intent.NONE}
      style={groupStyles}
    >
      <InputGroup
        type='text'
        noValidate
        disabled={disabled}
        placeholder={placeholder}
        autoComplete='off'
        intent={errorText ? Intent.DANGER : Intent.NONE}
        value={value}
        rightElement={CalendarPopover(value, step)}
        onBlur={(e) => validateInput(e, step)}
        onChange={onTextChange}
        {...props}

      />
    </FormGroup>
  )
}
